import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, EyeOff, PlugZap } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Chips, useAdmin } from "./ui";

const ConfirmPassword = ({ title, busy, onConfirm, onCancel }) => {
  const [pw, setPw] = useState("");
  return (
    <form className="pp-confirm" onSubmit={(e) => { e.preventDefault(); onConfirm(pw); }} data-testid="paypal-confirm">
      <label className="mu-field">Re-enter your password to {title}
        <input type="password" autoFocus required autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="paypal-confirm-password-input" />
      </label>
      <div className="req-actions">
        <button type="submit" className="btn gold" disabled={busy || !pw} data-testid="paypal-confirm-submit-button">{busy ? "Checking…" : "Confirm"}</button>
        <button type="button" className="btn light" onClick={onCancel} data-testid="paypal-confirm-cancel-button">Cancel</button>
      </div>
    </form>
  );
};

const Form = ({ cur }) => {
  const [f, setF] = useState({ client_id: cur.client_id, secret: "", mode: cur.mode });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState(null);
  const qc = useQueryClient();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setResult(null); };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const test = () => run(async () => {
    const { data } = await api.post("/admin/settings/paypal/test", f);
    setResult(data);
  });
  const finish = (pw) => run(async () => {
    if (confirm === "save") await api.put("/admin/settings/paypal", { ...f, password: pw });
    else await api.post("/admin/settings/paypal/disconnect", { password: pw });
    toast.success(confirm === "save" ? "PayPal connected" : "PayPal details removed");
    setConfirm(""); setF((x) => ({ ...x, secret: "" }));
    qc.invalidateQueries({ queryKey: ["admin"] });
  });

  return (
    <div className="ad-card pp-form" data-testid="paypal-form">
      <h3>PayPal account</h3>
      <p className="muted-sm">Find these in your PayPal Developer dashboard → Apps &amp; Credentials. Use Sandbox keys for testing, Live keys to accept real payments.</p>
      <Chips options={[["sandbox", "Sandbox (testing)"], ["live", "Live (real payments)"]]} value={f.mode} onChange={(m) => { setF({ ...f, mode: m }); setResult(null); }} testPrefix="paypal-mode" />
      <label className="mu-field">Client ID
        <input value={f.client_id} onChange={set("client_id")} autoComplete="off" spellCheck={false} placeholder="AbC123…" data-testid="paypal-client-id-input" />
      </label>
      <label className="mu-field">Secret
        <span className="pp-secret">
          <input type={show ? "text" : "password"} value={f.secret} onChange={set("secret")} autoComplete="new-password" spellCheck={false}
            placeholder={cur.secret_mask ? `Saved (${cur.secret_mask}) — leave blank to keep` : "Paste your secret"} data-testid="paypal-secret-input" />
          <button type="button" className="ad-icon-btn" onClick={() => setShow(!show)} aria-label={show ? "Hide secret" : "Show secret"} data-testid="paypal-secret-toggle">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
        </span>
      </label>
      {result && <p className={`pp-result ${result.ok ? "ok" : "bad"}`} data-testid="paypal-test-result">{result.message}</p>}
      {confirm ? <ConfirmPassword title={confirm === "save" ? "save PayPal details" : "remove PayPal details"} busy={busy} onConfirm={finish} onCancel={() => setConfirm("")} /> : (
        <div className="req-actions">
          <button type="button" className="btn light" onClick={test} disabled={busy} data-testid="paypal-test-button"><PlugZap size={15} /> {busy ? "Testing…" : "Test connection"}</button>
          <button type="button" className="btn gold" onClick={() => setConfirm("save")} disabled={busy || !f.client_id} data-testid="paypal-save-button">Save</button>
          {cur.source === "admin" && <button type="button" className="btn light" onClick={() => setConfirm("remove")} data-testid="paypal-remove-button">Remove</button>}
        </div>
      )}
    </div>
  );
};

export const PayPalForm = () => {
  const { data } = useAdmin("paypal-settings", "/admin/settings/paypal");
  return data ? <Form key={`${data.client_id}-${data.mode}-${data.secret_mask}`} cur={data} /> : null;
};
